/**
 * Top-level ErrorBoundary — catches synchronous render crashes (which
 * ProtectedRoute / Suspense do not) and shows a themed "Reload page"
 * fallback. Mounted above BrowserRouter in index.tsx. This is the seam
 * for a future central error reporter (see TODO in componentDidCatch).
 * Class component required — React 18 has no function-component
 * error-boundary API; only class component in src/.
 */

import { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Typography, Button, Stack } from '@mui/material';
import { ErrorOutline, Refresh } from '@mui/icons-material';
import { colors, designTokens } from '../../theme/theme';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Optional override for the fallback heading — useful for per-layout boundaries that want to scope the message. */
  fallbackTitle?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export default class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('[ErrorBoundary] render crash:', error, errorInfo.componentStack);

    // TODO: when a /api/client-errors endpoint exists, POST
    // { message, stack, componentStack, url, ts } here (no Sentry SDK —
    // runtime dep additions need founder sign-off).
  }

  handleReload = (): void => {
    // Hard reload clears corrupted in-memory state; cheaper than resetting the boundary.
    window.location.reload();
  };

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;

    return (
      <Box
        sx={{
          minHeight: '100vh',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: colors.background.default,
          p: 3,
        }}
      >
        <Box
          sx={{
            maxWidth: 480,
            width: '100%',
            p: { xs: 4, sm: 5 },
            borderRadius: 4,
            bgcolor: designTokens.glass.surface,
            border: `1px solid ${designTokens.glass.border}`,
            backdropFilter: designTokens.glass.blur,
            WebkitBackdropFilter: designTokens.glass.blur,
            textAlign: 'center',
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              mx: 'auto',
              mb: 2.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: colors.primary.gradient,
              boxShadow: '0 8px 32px rgba(232, 135, 92, 0.2)',
            }}
          >
            <ErrorOutline sx={{ fontSize: 36, color: '#fff' }} />
          </Box>

          <Typography
            variant="h5"
            sx={{ fontWeight: 700, color: colors.neutral[900], mb: 1 }}
          >
            {this.props.fallbackTitle || 'Something went wrong'}
          </Typography>

          <Typography
            variant="body2"
            sx={{ color: colors.neutral[600], mb: 3.5, lineHeight: 1.6 }}
          >
            CoffeeMates hit an unexpected error. Reloading the page usually fixes it.
            If this keeps happening, please let us know.
          </Typography>

          <Stack spacing={1.5} alignItems="center">
            <Button
              variant="contained"
              size="large"
              startIcon={<Refresh />}
              onClick={this.handleReload}
              sx={{
                px: 4,
                py: 1.25,
                background: colors.primary.gradient,
                color: '#fff',
                fontWeight: 600,
                borderRadius: 2,
                '&:hover': {
                  background: `linear-gradient(135deg, ${colors.primary.light} 0%, ${colors.primary.main} 100%)`,
                  boxShadow: '0 8px 32px rgba(232, 135, 92, 0.2)',
                },
              }}
            >
              Reload page
            </Button>
          </Stack>
        </Box>
      </Box>
    );
  }
}
